/**
 * 🏷️ **The names on the map** — T189. **HAND-AUTHORED, and checked.**
 *
 * 🎯 **haugur, 2026-09-11:** *"on this map you will place names of the biggest
 * landmarks in iceland and town names, like Gullfoss, Akureyri, Hafnarfjordur,
 * reykjavík, vatnajökull etc etc."*
 *
 * ## 🔴 Why this is a hand-written list and not a query
 *
 * **Iceland has eight places called Gullfoss.** Seven are waterfalls in the
 * Westfjords; the famous one is in Haukadalur. There are **three Akureyri** —
 * the town, and two sand spits. Two Geysir, two Hekla, five Goðafoss.
 * `search.ts` has warned about exactly this since it was written: *"Iceland has
 * eleven places called Hamarinn."*
 *
 * 🔴 **So a map that looked names up would put Gullfoss in eight places at
 * once, and label two sandbanks Akureyri** — and it would look deliberate.
 *
 * ## ✅ How each of these was checked, 2026-09-11
 *
 * Every row below was resolved by asking the national place-name register for
 * **the nearest feature of that name to a position known independently**, and
 * printing **how far off it was**. Towns came from `nafnberi = 'byggðarkjarni'`,
 * which is unique per town. The natural features all landed within **8 km**,
 * and all but three within **1 km**.
 *
 * ⚠️ **The three that are further out are not errors and must not be
 * "corrected":** Látrabjarg is 7.8 km off because it is a **14 km cliff**,
 * Lagarfljót 2.9 km because it is a **53 km lake**, and Þingvallavatn 1.6 km
 * because it is **84 km²**. A single point on a long thing is a point on a long
 * thing.
 *
 * 👉 **To add one:** find it in Dev Tools' search, check the coordinate is the
 * feature you meant and not its namesake, and add a row. **Never paste a
 * coordinate you have not looked at.**
 */

/** ISN93 easting and northing — the same pair the teleport box takes. */
export interface Landmark {
  readonly name: string;
  /** ISN93 easting. */
  readonly x: number;
  /** ISN93 northing. ⚠️ Not world z — the map converts at its own boundary. */
  readonly y: number;
  /**
   * What it is, which decides how it is drawn and **at what zoom it appears**.
   *
   * ⚠️ **`town` and `nature` are drawn differently on purpose** (V7 — colour is
   * never the only signal): a town has a dot, a natural feature does not.
   */
  readonly kind: 'town' | 'nature';
  /**
   * Smaller shows sooner. 1 is always on screen; 3 only when zoomed in.
   *
   * 🔴 **Without this the country view is a wall of overlapping words.** Fifty
   * labels over 500 km is one every 10 km, and at country zoom 10 km is a
   * dozen pixels.
   */
  readonly rank: 1 | 2 | 3;
}

export const LANDMARKS: readonly Landmark[] = [
  // ── the ones anybody would name ────────────────────────────────────────────
  { name: 'Reykjavík', x: 360987, y: 407037, kind: 'town', rank: 1 },
  { name: 'Akureyri', x: 540814, y: 576440, kind: 'town', rank: 1 },
  { name: 'Vatnajökull', x: 605559, y: 439431, kind: 'nature', rank: 1 },
  { name: 'Ísafjörður', x: 312422, y: 625148, kind: 'town', rank: 1 },
  { name: 'Egilsstaðir', x: 714838, y: 537040, kind: 'town', rank: 1 },
  { name: 'Höfn', x: 683864, y: 422184, kind: 'town', rank: 1 },
  { name: 'Selfoss', x: 401835, y: 382522, kind: 'town', rank: 1 },
  { name: 'Hekla', x: 467239, y: 387778, kind: 'nature', rank: 1 },
  { name: 'Mývatn', x: 592548, y: 568726, kind: 'nature', rank: 1 },
  { name: 'Þingvellir', x: 397333, y: 419181, kind: 'nature', rank: 1 },

  /* 🏠 **Home.** The only one of these you can walk to. */
  { name: 'Hafnarfjörður', x: 355032, y: 398027, kind: 'town', rank: 1 },

  // ── the rest of the towns ──────────────────────────────────────────────────
  { name: 'Kópavogur', x: 360247, y: 403015, kind: 'town', rank: 3 },
  { name: 'Garðabær', x: 357690, y: 402012, kind: 'town', rank: 3 },
  { name: 'Mosfellsbær', x: 369061, y: 409842, kind: 'town', rank: 3 },
  { name: 'Keflavík', x: 325604, y: 393699, kind: 'town', rank: 2 },
  { name: 'Grindavík', x: 331129, y: 375527, kind: 'town', rank: 2 },
  { name: 'Sandgerði', x: 319197, y: 397930, kind: 'town', rank: 3 },
  { name: 'Þorlákshöfn', x: 382767, y: 374539, kind: 'town', rank: 3 },
  { name: 'Hveragerði', x: 392987, y: 390455, kind: 'town', rank: 3 },
  { name: 'Akranes', x: 351876, y: 427743, kind: 'town', rank: 2 },
  { name: 'Borgarnes', x: 360553, y: 452905, kind: 'town', rank: 2 },
  { name: 'Stykkishólmur', x: 324485, y: 513364, kind: 'town', rank: 2 },
  { name: 'Patreksfjörður', x: 270090, y: 575488, kind: 'town', rank: 3 },
  { name: 'Blönduós', x: 440778, y: 574269, kind: 'town', rank: 3 },
  { name: 'Sauðárkrókur', x: 470516, y: 583027, kind: 'town', rank: 2 },
  { name: 'Siglufjörður', x: 504040, y: 628153, kind: 'town', rank: 2 },
  { name: 'Dalvík', x: 521245, y: 608443, kind: 'town', rank: 3 },
  { name: 'Húsavík', x: 575119, y: 617419, kind: 'town', rank: 2 },
  { name: 'Seyðisfjörður', x: 733159, y: 538451, kind: 'town', rank: 2 },
  { name: 'Neskaupstaður', x: 748638, y: 526844, kind: 'town', rank: 3 },
  { name: 'Vík', x: 499710, y: 323734, kind: 'town', rank: 2 },
  { name: 'Hvolsvöllur', x: 439549, y: 361490, kind: 'town', rank: 3 },
  { name: 'Hella', x: 431261, y: 371044, kind: 'town', rank: 3 },
  { name: 'Vestmannaeyjar', x: 436155, y: 326471, kind: 'town', rank: 2 },

  // ── the natural landmarks ──────────────────────────────────────────────────
  { name: 'Gullfoss', x: 445871, y: 425486, kind: 'nature', rank: 2 },
  { name: 'Geysir', x: 437128, y: 424148, kind: 'nature', rank: 2 },
  { name: 'Dettifoss', x: 619582, y: 593263, kind: 'nature', rank: 2 },
  { name: 'Goðafoss', x: 566628, y: 576923, kind: 'nature', rank: 3 },
  { name: 'Skógafoss', x: 474553, y: 336456, kind: 'nature', rank: 3 },
  { name: 'Seljalandsfoss', x: 450969, y: 346023, kind: 'nature', rank: 3 },
  { name: 'Dynjandi', x: 307482, y: 588098, kind: 'nature', rank: 3 },
  { name: 'Jökulsárlón', x: 635699, y: 400002, kind: 'nature', rank: 2 },
  { name: 'Ásbyrgi', x: 613223, y: 615736, kind: 'nature', rank: 3 },
  { name: 'Landmannalaugar', x: 496982, y: 387571, kind: 'nature', rank: 3 },
  { name: 'Askja', x: 605676, y: 505563, kind: 'nature', rank: 3 },
  { name: 'Dyrhólaey', x: 494083, y: 322250, kind: 'nature', rank: 3 },
  /* ⚠️ 7.8 km from the point it was checked against — it is a 14 km cliff. */
  { name: 'Látrabjarg', x: 251910, y: 565869, kind: 'nature', rank: 3 },
  { name: 'Snæfellsjökull', x: 273394, y: 487080, kind: 'nature', rank: 2 },
  { name: 'Eyjafjallajökull', x: 470007, y: 346986, kind: 'nature', rank: 2 },
  { name: 'Þingvallavatn', x: 396316, y: 410663, kind: 'nature', rank: 3 },
  /* ⚠️ 2.9 km off — Lagarfljót is a 53 km lake. */
  { name: 'Lagarfljót', x: 707864, y: 537175, kind: 'nature', rank: 3 },
  /* ❄️ The four ice caps below are the CENTROID of the named glacier polygon,
     not a point feature — the polygon is unambiguous because there is only
     one Langjökull over 50 km². ⚠️ Written from memory in the first draft and
     replaced with the real numbers; the largest error was 13 km. */
  { name: 'Langjökull', x: 444397, y: 463992, kind: 'nature', rank: 2 },
  { name: 'Hofsjökull', x: 508976, y: 479286, kind: 'nature', rank: 2 },
  { name: 'Mýrdalsjökull', x: 494551, y: 350644, kind: 'nature', rank: 2 },
  { name: 'Drangajökull', x: 354219, y: 632077, kind: 'nature', rank: 3 },
];
